import { useEffect, useState } from 'react'
import { IDoesFilterPassParams } from 'ag-grid-community'
import { CustomFilterProps, useGridFilter } from 'ag-grid-react'
import { BulkSheetFilter, FilterFooter, FilterInput } from '../common'
import {
  styled,
  Box,
  Checkbox,
  FormControlLabel,
  Typography,
} from '@mui/material'
import { ICON_SIZE } from '../../../../BulkSheet/const'
import { CustomEnumValue } from '../../../../../../lib/commons/appFunction'

export type SelectFilter = {
  values: string[]
  includeBlank: boolean
}

type Props = CustomFilterProps & {
  customEnumCode: string
  getValue: (option: any) => string
  getLabel: (option: any) => string
  valueGetter?: (params: any) => any
  valueFormatter: (params: any) => string
  sortValues: (options: any, context: any) => any
}

const buildSelectFilterModel = (
  values: string[],
  includeBlank: boolean,
  allOptionValues: string[]
): SelectFilter | null => {
  const isAllSelected =
    allOptionValues.length > 0 &&
    values.length === allOptionValues.length &&
    includeBlank
  return isAllSelected ? null : { values, includeBlank }
}

export const CustomEnumFilter = ({
  model,
  onModelChange,
  customEnumCode,
  getValue,
  getLabel,
  valueGetter,
  valueFormatter,
  sortValues,
  colDef,
  api,
  context,
}: Props) => {
  // UI-local state
  const [text, setText] = useState('')
  const [options, setOptions] = useState<any[]>([])

  const customEnums = !customEnumCode
    ? []
    : Array.isArray(context[customEnumCode])
      ? context[customEnumCode]
      : ([].concat(...context[customEnumCode].values()) as CustomEnumValue[])

  // Derive state from model (single source of truth)
  const allOptionValues = options.map(getValue)
  const values = model?.values ?? allOptionValues
  const includeBlank = model?.includeBlank ?? true
  const isAllSelected =
    !model || (values.length === allOptionValues.length && includeBlank)
  const isAllDeselected = model !== null && values.length === 0 && !includeBlank

  // eslint-disable-next-line react-hooks/exhaustive-deps -- マウント時のみ実行
  useEffect(() => {
    setOptions(getOptions())
  }, [])

  useGridFilter({
    doesFilterPass(params: IDoesFilterPassParams) {
      // Don't filter new rows
      if (params.data.isAdded) return true
      if (!model) return true

      const selectValue = valueGetter
        ? valueGetter(params)
        : api.getCellValue({ rowNode: params.node, colKey: colDef.field! })
      if (model.includeBlank && !selectValue) return true
      if (
        Array.isArray(selectValue) &&
        selectValue.some(v => model.values.includes(v))
      ) {
        return true
      } else if (model.values.includes(selectValue)) {
        return true
      }
      return false
    },

    afterGuiAttached() {
      setOptions(getOptions())
    },

    getModelAsString() {
      if (!model) return ''
      let labelList: string[] = []
      model.includeBlank && labelList.push('(Blank)')
      labelList = labelList.concat(
        options
          .filter(option => model.values.includes(getValue(option)))
          .map(option => getLabel(option))
      )
      return labelList.join(',')
    },
  })

  const getOptions = () => {
    const initialOptions: any[] = []
    api.forEachNode(node => {
      initialOptions.push(
        valueGetter
          ? valueGetter({ node })
          : api.getCellValue({ rowNode: node, colKey: colDef.field! })
      )
    })
    const newOptionValues: string[] = []
    const newOptions: any[] = []
    initialOptions
      .filter(option => !!option)
      .forEach(option => {
        if (Array.isArray(option)) {
          option.forEach(o => {
            if (!newOptionValues.includes(o)) {
              newOptions.push(customEnums.find(v => v.value === o))
              newOptionValues.push(o)
            }
          })
        } else if (!newOptionValues.includes(option)) {
          const customEnum = customEnums.find(v => v.value === option)
          if (customEnum) {
            newOptions.push(customEnum)
            newOptionValues.push(option)
          }
        }
      })
    return sortValues ? sortValues(newOptions, context) : newOptions
  }

  // Helpers for model updates
  const updateValues = (newValues: string[]) => {
    onModelChange(
      buildSelectFilterModel(newValues, includeBlank, allOptionValues)
    )
  }

  const updateIncludeBlank = (newIncludeBlank: boolean) => {
    onModelChange(
      buildSelectFilterModel(values, newIncludeBlank, allOptionValues)
    )
  }

  return (
    <BulkSheetFilter>
      <FilterInput
        value={text}
        onChange={e => setText(e.target.value)}
        placeholder={'Search...'}
        sx={{ height: 22 }}
      />
      <SelectFilterArea>
        <SelectFilterFormControl
          label="(Select All)"
          control={
            <Checkbox
              checked={isAllSelected}
              indeterminate={!isAllSelected && !isAllDeselected}
              onChange={e =>
                e.target.checked
                  ? onModelChange(null)
                  : onModelChange({ values: [], includeBlank: false })
              }
              disableRipple={true}
            />
          }
        />
        <CheckboxArea>
          {!text && (
            <SelectFilterFormControl
              label="(Blanks)"
              control={
                <Checkbox
                  checked={includeBlank}
                  onChange={e => updateIncludeBlank(e.target.checked)}
                  disableRipple={true}
                />
              }
            />
          )}
          {options
            .filter(
              option =>
                !text ||
                (valueFormatter
                  ? valueFormatter({
                      colDef,
                      value: getLabel(option),
                    }).includes(text)
                  : getLabel(option).includes(text))
            )
            .map(option => {
              const optionValue = getValue(option)
              return (
                <SelectFilterFormControl
                  key={`select-filter-${optionValue}`}
                  label={
                    <FilterLabel
                      sx={{
                        backgroundColor: option.backgroundColor ?? 'inherit',
                      }}
                    >
                      {option.iconUrl && (
                        <img
                          src={option.iconUrl}
                          style={{
                            height: `${ICON_SIZE}px`,
                            width: `${ICON_SIZE}px`,
                            borderRadius: '50%',
                          }}
                        />
                      )}
                      <Typography sx={{ padding: '0 3px' }}>
                        {valueFormatter
                          ? valueFormatter({
                              colDef,
                              value: getLabel(option),
                            })
                          : getLabel(option)}
                      </Typography>
                    </FilterLabel>
                  }
                  control={
                    <Checkbox
                      checked={values.includes(optionValue)}
                      onChange={e => {
                        if (e.target.checked) {
                          updateValues([...values, optionValue])
                        } else {
                          updateValues(values.filter(v => v !== optionValue))
                        }
                      }}
                      disableRipple={true}
                    />
                  }
                />
              )
            })}
        </CheckboxArea>
      </SelectFilterArea>
      <FilterFooter loading={false} onClick={() => onModelChange(null)} />
    </BulkSheetFilter>
  )
}

export const SelectFilterFormControl = styled(FormControlLabel)({
  margin: 0,
})

export const SelectFilterArea = styled(Box)({
  width: '100%',
  display: 'flex',
  justifyContent: 'start',
  flexDirection: 'column',
  margin: '0 3px',
})

export const CheckboxArea = styled(Box)({
  display: 'flex',
  justifyContent: 'start',
  flexDirection: 'column',
  marginLeft: '20px',
})

export const FilterLabel = styled(Box)({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  borderRadius: '2px',
})
